import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
} from 'react-bootstrap';
import ResizableTable from '../common/ResizableTable';
import RecycleBinApi from '../../api/RecycleBinApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { formatDateTime } from '../../utils/formatUtils';
import './RecycleBin.css';

/** Same batch size as ListViewLayout infinite scroll */
const BATCH_SIZE = 200;
const DELETE_CLIENT_BATCH_SIZE = 50;

function cell(value, displayValue) {
  const v = value ?? '';
  const d = displayValue ?? v;
  return {
    value: v,
    displayValue: d === '' ? '—' : String(d),
  };
}

function ConfirmModal({
  show,
  title,
  message,
  confirmLabel,
  variant,
  onHide,
  onConfirm,
  busy,
  error,
  extraBody,
}) {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="mb-0">{message}</p>
        {extraBody}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant={variant || 'primary'} onClick={onConfirm} disabled={busy}>
          {busy ? 'Please wait…' : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const RecycleBin = () => {
  const api = useMemo(() => new RecycleBinApi(), []);
  const abortControllerRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [objects, setObjects] = useState([]);
  const [objectName, setObjectName] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [restoreWithParents, setRestoreWithParents] = useState(false);
  const [missingParents, setMissingParents] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);
  const [bulkRestoreWithParents, setBulkRestoreWithParents] = useState(false);
  const [bulkRestoreBusy, setBulkRestoreBusy] = useState(false);
  const [bulkRestoreError, setBulkRestoreError] = useState('');
  const [bulkRestoreResult, setBulkRestoreResult] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState({
    total: 0,
    processed: 0,
    deleted: 0,
    errors: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ObjectConfigApi.fetchObjectConfigs();
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setObjects(res.data.filter((o) => !o.is_deleted));
        }
      } catch (e) {
        console.warn('RecycleBin: could not load objects', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEntries = useCallback(
    async (pageToLoad = 1, append = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      try {
        const res = await api.fetchEntries(
          {
            limit: BATCH_SIZE,
            offset: (pageToLoad - 1) * BATCH_SIZE,
            objectName: objectName || undefined,
            search: debouncedSearch || undefined,
          },
          { signal: abortController.signal }
        );

        if (abortController.signal.aborted) return;

        if (res?.data?.success) {
          const newRows = res.data.data || [];
          const newTotal = res.data.total ?? 0;
          setTotal(newTotal);
          setPage(pageToLoad);

          if (append) {
            setRows((prev) => {
              const updated = [...prev, ...newRows];
              setHasMore(updated.length < newTotal && newRows.length > 0);
              return updated;
            });
          } else {
            setRows(newRows);
            setHasMore(newRows.length < newTotal && newRows.length > 0);
          }
        } else {
          setError(res?.data?.message || 'Failed to load recycle bin');
          if (!append) {
            setRows([]);
            setTotal(0);
          }
          setHasMore(false);
        }
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setError(
          e?.response?.data?.message || e?.message || 'Failed to load recycle bin'
        );
        if (!append) {
          setRows([]);
          setTotal(0);
        }
        setHasMore(false);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [api, objectName, debouncedSearch]
  );

  // Object / search filter change → reset and fetch first page server-side
  useEffect(() => {
    setSelectedRows([]);
    setPage(1);
    setHasMore(true);
    loadEntries(1, false);
  }, [objectName, debouncedSearch, loadEntries]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !hasMore) return;
    loadEntries(page + 1, true);
  }, [loading, isLoadingMore, hasMore, page, loadEntries]);

  const refreshEntries = useCallback(() => {
    setSelectedRows([]);
    return loadEntries(1, false);
  }, [loadEntries]);

  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        id: cell(row.id),
        object_label: cell(row.object_label || row.object_name),
        record_name: cell(row.record_name),
        deleted_by_name: cell(row.deleted_by_name, row.deleted_by_name || row.deleted_by_email),
        deleted_at: cell(row.deleted_at, formatDateTime(row.deleted_at)),
      })),
    [rows]
  );

  const columns = useMemo(
    () => [
      { field: 'object_label', label: 'Object', sortable: true },
      { field: 'record_name', label: 'Record', sortable: true },
      { field: 'deleted_by_name', label: 'Deleted By', sortable: true },
      { field: 'deleted_at', label: 'Deleted At', sortable: true },
    ],
    []
  );

  // Row actions rendered via ResizableTable dropdown (Restore / Delete Forever)
  const rowActions = useMemo(
    () => [
      {
        label: 'Restore',
        icon: 'fas fa-undo',
        onClick: (record) => {
          const id = record?.id?.value;
          if (!id) return;
          const row = rows.find((r) => r.id === id);
          if (!row) return;
          setActionError('');
          setRestoreWithParents(false);
          setMissingParents([]);
          setActionTarget({ type: 'restore', row });
        },
      },
      {
        label: 'Delete Forever',
        icon: 'fas fa-trash-alt',
        onClick: (record) => {
          const id = record?.id?.value;
          if (!id) return;
          const row = rows.find((r) => r.id === id);
          if (!row) return;
          setActionError('');
          setActionTarget({ type: 'delete', row });
        },
      },
    ],
    [rows]
  );

  const handleConfirmAction = async () => {
    if (!actionTarget?.row?.id) return;
    setActionBusy(true);
    setActionError('');
    try {
      if (actionTarget.type === 'restore') {
        const res = await api.restoreEntry(actionTarget.row.id, {
          restoreParents: restoreWithParents,
        });
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Restore failed');
        }
        setMissingParents([]);
        setRestoreWithParents(false);
        setSuccess(
          restoreWithParents && missingParents.length > 0
            ? 'Record and required parent record(s) restored successfully.'
            : 'Record restored successfully.'
        );
      } else {
        const res = await api.permanentDeleteEntry(actionTarget.row.id);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || 'Permanent delete failed');
        }
        setSuccess('Record permanently deleted.');
      }
      setActionTarget(null);
      await refreshEntries();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.code === 'RESTORE_PARENT_REQUIRED' && Array.isArray(data.missingParents)) {
        const restorable = data.missingParents.filter((p) => p.inRecycleBin);
        const gone = data.missingParents.filter((p) => p.permanentlyDeleted);
        setMissingParents(data.missingParents);
        if (gone.length > 0) {
          setActionError(
            data.message ||
              'A required parent was permanently deleted and cannot be restored.'
          );
          setRestoreWithParents(false);
        } else if (restorable.length > 0) {
          setRestoreWithParents(true);
          setActionError('');
        } else {
          setActionError(data.message || 'Restore blocked by missing parent records.');
        }
      } else {
        setActionError(data?.message || e?.message || 'Action failed');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const selectedEntryRows = useMemo(
    () => rows.filter((row) => selectedRows.includes(row.id)),
    [rows, selectedRows]
  );

  const openBulkRestore = () => {
    if (selectedRows.length === 0) return;
    setBulkRestoreError('');
    setBulkRestoreResult(null);
    setBulkRestoreWithParents(false);
    setBulkRestoreOpen(true);
  };

  const openBulkDelete = () => {
    if (selectedRows.length === 0) return;
    setBulkDeleteError('');
    setBulkDeleteResult(null);
    setDeleteProgress({
      total: selectedRows.length,
      processed: 0,
      deleted: 0,
      errors: 0,
    });
    setBulkDeleteOpen(true);
  };

  const closeBulkDelete = async () => {
    if (bulkDeleteBusy) return;
    const hadResult = !!bulkDeleteResult;
    setBulkDeleteOpen(false);
    setBulkDeleteResult(null);
    setBulkDeleteError('');
    setDeleteProgress({ total: 0, processed: 0, deleted: 0, errors: 0 });
    if (hadResult) {
      setSelectedRows([]);
      await refreshEntries();
    }
  };

  const handleBulkRestore = async () => {
    const idsToRestore =
      bulkRestoreResult?.errors?.length > 0
        ? bulkRestoreResult.errors.map((err) => err.id).filter(Boolean)
        : selectedRows;
    if (idsToRestore.length === 0) return;

    setBulkRestoreBusy(true);
    setBulkRestoreError('');
    try {
      const res = await api.bulkRestoreEntries(idsToRestore, {
        continueOnError: true,
        restoreParents: bulkRestoreWithParents,
      });
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || 'Bulk restore failed');
      }
      const result = res.data.data || {};
      setBulkRestoreResult(result);
      if ((result.restored || 0) > 0) {
        setSuccess(
          `Restored ${result.restored} record${result.restored !== 1 ? 's' : ''} successfully.`
        );
        const restoredIds = new Set(
          idsToRestore.filter(
            (id) => !result.errors?.some((err) => err.id === id)
          )
        );
        setSelectedRows((prev) => prev.filter((id) => !restoredIds.has(id)));
        await refreshEntries();
      }
      if ((result.errors || []).length === 0) {
        setBulkRestoreOpen(false);
        setBulkRestoreResult(null);
        setBulkRestoreWithParents(false);
      }
    } catch (e) {
      setBulkRestoreError(
        e?.response?.data?.message || e?.message || 'Bulk restore failed'
      );
    } finally {
      setBulkRestoreBusy(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedRows.length === 0) return;

    const idsToDelete = [...selectedRows];
    setBulkDeleteBusy(true);
    setBulkDeleteError('');
    setBulkDeleteResult(null);
    setDeleteProgress({
      total: idsToDelete.length,
      processed: 0,
      deleted: 0,
      errors: 0,
    });

    try {
      const aggregate = {
        total: idsToDelete.length,
        deleted: 0,
        errors: [],
      };

      for (let i = 0; i < idsToDelete.length; i += DELETE_CLIENT_BATCH_SIZE) {
        const batch = idsToDelete.slice(i, i + DELETE_CLIENT_BATCH_SIZE);
        const res = await api.bulkPermanentDeleteEntries(batch, {
          continueOnError: true,
        });

        if (!res?.data?.success) {
          const msg = res?.data?.message || 'Bulk permanent delete failed';
          batch.forEach((id) => {
            aggregate.errors.push({ id, error: msg });
          });
        } else {
          const result = res.data.data || {};
          aggregate.deleted += result.deleted || 0;
          if (Array.isArray(result.errors) && result.errors.length > 0) {
            aggregate.errors.push(...result.errors);
          }
        }

        setDeleteProgress({
          total: idsToDelete.length,
          processed: Math.min(i + batch.length, idsToDelete.length),
          deleted: aggregate.deleted,
          errors: aggregate.errors.length,
        });
      }

      setBulkDeleteResult({
        deleted: aggregate.deleted,
        total: aggregate.total,
        errors: aggregate.errors,
        apiError: null,
      });

      if (aggregate.deleted > 0) {
        setSuccess(
          `Permanently deleted ${aggregate.deleted} record${
            aggregate.deleted !== 1 ? 's' : ''
          }.`
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Bulk permanent delete failed';
      setBulkDeleteError(msg);
      setBulkDeleteResult({
        deleted: 0,
        total: idsToDelete.length,
        errors: [],
        apiError: msg,
      });
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  const headerActions = useMemo(
    () => [
      {
        label:
          selectedRows.length > 0
            ? `Restore (${selectedRows.length})`
            : 'Restore',
        icon: 'fas fa-undo',
        variant: 'outline-primary',
        disabled: selectedRows.length === 0,
        onClick: openBulkRestore,
        className: 'recycle-bin-bulk-restore-btn',
      },
      {
        label:
          selectedRows.length > 0
            ? `Delete Forever (${selectedRows.length})`
            : 'Delete Forever',
        icon: 'fas fa-trash-alt',
        variant: 'outline-danger',
        disabled: selectedRows.length === 0,
        onClick: openBulkDelete,
        className: 'recycle-bin-bulk-delete-btn',
      },
    ],
    [selectedRows.length]
  );

  return (
    <Container fluid className="recycle-bin-page mt-4 px-4 pb-4">
      <Row className="mb-3">
        <Col>
          <h4 className="fw-semibold mb-1">Recycle Bin</h4>
          <p className="text-muted small mb-0">
            Restore records you deleted, or permanently remove them.
          </p>
        </Col>
      </Row>

      <Card className="border-0 bg-light mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Object</Form.Label>
              <Form.Select
                size="sm"
                value={objectName}
                onChange={(e) => {
                  setObjectName(e.target.value);
                }}
              >
                <option value="">All objects</option>
                {objects.map((obj) => (
                  <option key={obj.object_name} value={obj.object_name}>
                    {obj.object_label || obj.object_name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-3">
          <ResizableTable
            className="recycle-bin-table"
            title="Deleted Records"
            data={tableData}
            columns={columns}
            loading={loading && rows.length === 0}
            searchTerm={search}
            onSearchChange={(value) => {
              setSearch(value);
            }}
            currentPage={1}
            totalRecords={total}
            recordsPerPage={tableData.length || BATCH_SIZE}
            onPageChange={() => {}}
            showActions
            actions={rowActions}
            showNewButton={false}
            showPagination={false}
            searchPlaceholder="Search record or object…"
            emptyMessage="No deleted records in your recycle bin."
            onRefresh={refreshEntries}
            selectedRows={selectedRows}
            onRowSelectionChange={setSelectedRows}
            headerActions={headerActions}
            fitViewportHeight
            onLoadMore={loadMore}
            hasMoreRecords={hasMore}
            loadingMore={isLoadingMore}
          />
        </Card.Body>
      </Card>

      <ConfirmModal
        show={bulkRestoreOpen}
        title="Restore selected records"
        message={
          bulkRestoreResult?.errors?.length
            ? `Restored ${bulkRestoreResult.restored || 0} of ${bulkRestoreResult.total || selectedRows.length} record(s). Some records could not be restored.`
            : `Restore ${selectedRows.length} selected record${selectedRows.length !== 1 ? 's' : ''}?`
        }
        confirmLabel={
          bulkRestoreResult?.errors?.length
            ? bulkRestoreWithParents
              ? 'Retry with parents'
              : 'Close'
            : bulkRestoreWithParents
              ? 'Restore with parents'
              : 'Restore'
        }
        variant="primary"
        onHide={() => {
          if (bulkRestoreBusy) return;
          setBulkRestoreOpen(false);
          setBulkRestoreResult(null);
          setBulkRestoreWithParents(false);
        }}
        onConfirm={() => {
          if (bulkRestoreResult?.errors?.length && !bulkRestoreWithParents) {
            setBulkRestoreOpen(false);
            setBulkRestoreResult(null);
            return;
          }
          handleBulkRestore();
        }}
        busy={bulkRestoreBusy}
        error={bulkRestoreError}
        extraBody={
          <>
            {!bulkRestoreResult && selectedEntryRows.length > 0 && (
              <ul className="small text-muted mt-3 mb-0 ps-3" style={{ maxHeight: 160, overflowY: 'auto' }}>
                {selectedEntryRows.slice(0, 10).map((row) => (
                  <li key={row.id}>
                    {row.record_name || row.record_id} ({row.object_label || row.object_name})
                  </li>
                ))}
                {selectedEntryRows.length > 10 && (
                  <li>…and {selectedEntryRows.length - 10} more</li>
                )}
              </ul>
            )}
            {!bulkRestoreResult && (
              <Form.Check
                type="checkbox"
                id="bulk-restore-with-parents"
                className="mt-3"
                label="Also restore required parent records when needed"
                checked={bulkRestoreWithParents}
                onChange={(e) => setBulkRestoreWithParents(e.target.checked)}
                disabled={bulkRestoreBusy}
              />
            )}
            {bulkRestoreResult?.errors?.length > 0 && (
              <div className="mt-3">
                <p className="small fw-semibold mb-2">Failed records:</p>
                <ul className="small text-muted mb-0 ps-3" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {bulkRestoreResult.errors.map((err) => {
                    const row = rows.find((r) => r.id === err.id);
                    const label = row?.record_name || err.id;
                    const needsParents =
                      err.code === 'RESTORE_PARENT_REQUIRED' &&
                      Array.isArray(err.missingParents) &&
                      err.missingParents.some((p) => p.inRecycleBin);
                    return (
                      <li key={err.id}>
                        {label}: {err.error}
                        {needsParents && !bulkRestoreWithParents && (
                          <span className="d-block text-warning">
                            Enable &quot;restore required parents&quot; and retry.
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {bulkRestoreResult.errors.some(
                  (e) =>
                    e.code === 'RESTORE_PARENT_REQUIRED' &&
                    Array.isArray(e.missingParents) &&
                    e.missingParents.some((p) => p.inRecycleBin)
                ) &&
                  !bulkRestoreWithParents && (
                    <Form.Check
                      type="checkbox"
                      id="bulk-restore-retry-with-parents"
                      className="mt-3"
                      label="Also restore required parent records when needed"
                      checked={bulkRestoreWithParents}
                      onChange={(e) => setBulkRestoreWithParents(e.target.checked)}
                      disabled={bulkRestoreBusy}
                    />
                  )}
              </div>
            )}
          </>
        }
      />

      <Modal
        show={bulkDeleteOpen}
        onHide={closeBulkDelete}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!bulkDeleteBusy}>
          <Modal.Title>
            {bulkDeleteResult
              ? 'Delete Forever Result'
              : bulkDeleteBusy
                ? 'Deleting Records'
                : 'Permanently delete selected records'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bulkDeleteError && !bulkDeleteResult && (
            <Alert variant="danger">{bulkDeleteError}</Alert>
          )}
          {bulkDeleteResult ? (
            <div>
              {bulkDeleteResult.apiError ? (
                <Alert variant="danger" className="mb-0">
                  {bulkDeleteResult.apiError}
                </Alert>
              ) : (
                <>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="text-success">
                      <strong>Deleted:</strong> {bulkDeleteResult.deleted} of{' '}
                      {bulkDeleteResult.total}
                    </div>
                    {bulkDeleteResult.errors?.length > 0 && (
                      <div className="text-danger">
                        <strong>Failed:</strong>{' '}
                        {bulkDeleteResult.errors.length}
                      </div>
                    )}
                  </div>
                  {bulkDeleteResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <strong className="text-muted">Reasons:</strong>
                      <ul className="list-unstyled mb-0 mt-1 small">
                        {bulkDeleteResult.errors.slice(0, 10).map((err, idx) => {
                          const row = rows.find((r) => r.id === err.id);
                          const label = row?.record_name || err.id;
                          return (
                            <li key={err.id || idx} className="text-danger">
                              {label}: {err.error}
                            </li>
                          );
                        })}
                        {bulkDeleteResult.errors.length > 10 && (
                          <li className="text-muted">
                            …and {bulkDeleteResult.errors.length - 10} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : bulkDeleteBusy ? (
            <div className="text-center">
              <div className="spinner-border text-danger mb-3" role="status">
                <span className="visually-hidden">Deleting...</span>
              </div>
              <p className="mb-2">
                Deleting {deleteProgress.total} record
                {deleteProgress.total !== 1 ? 's' : ''}...
              </p>
              <div className="small text-muted">
                Processed {deleteProgress.processed} / {deleteProgress.total} ·
                Deleted {deleteProgress.deleted} · Failed{' '}
                {deleteProgress.errors}
              </div>
            </div>
          ) : (
            <div>
              <p className="mb-0">
                Permanently delete {selectedRows.length} selected record
                {selectedRows.length !== 1 ? 's' : ''}? This cannot be undone.
              </p>
              {selectedEntryRows.length > 0 && (
                <ul
                  className="small text-muted mt-3 mb-0 ps-3"
                  style={{ maxHeight: 160, overflowY: 'auto' }}
                >
                  {selectedEntryRows.slice(0, 10).map((row) => (
                    <li key={row.id}>
                      {row.record_name || row.record_id} (
                      {row.object_label || row.object_name})
                    </li>
                  ))}
                  {selectedEntryRows.length > 10 && (
                    <li>…and {selectedEntryRows.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {bulkDeleteResult ? (
            <Button variant="primary" onClick={closeBulkDelete}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={closeBulkDelete}
                disabled={bulkDeleteBusy}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleBulkPermanentDelete}
                disabled={bulkDeleteBusy || selectedRows.length === 0}
              >
                {bulkDeleteBusy ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Deleting...
                  </>
                ) : (
                  'Delete Forever'
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={actionTarget?.type === 'restore'}
        title="Restore record"
        message={
          actionTarget?.row
            ? restoreWithParents && missingParents.length > 0
              ? `Restore "${actionTarget.row.record_name || 'this record'}" and the required parent record(s) listed below?`
              : `Restore "${actionTarget.row.record_name || 'this record'}" on ${
                  actionTarget.row.object_label || actionTarget.row.object_name
                }?`
            : ''
        }
        confirmLabel={
          restoreWithParents && missingParents.length > 0
            ? 'Restore with parents'
            : 'Restore'
        }
        variant="primary"
        onHide={() => {
          if (actionBusy) return;
          setRestoreWithParents(false);
          setMissingParents([]);
          setActionTarget(null);
        }}
        onConfirm={handleConfirmAction}
        busy={actionBusy}
        error={actionError}
        extraBody={
          restoreWithParents && missingParents.length > 0 ? (
            <ul className="small text-muted mt-3 mb-0 ps-3">
              {missingParents
                .filter((p) => p.inRecycleBin)
                .map((p) => (
                  <li key={`${p.objectName}-${p.recordId}`}>
                    {p.fieldLabel || p.fieldName}: {p.recordName || p.recordId} (
                    {p.objectName})
                  </li>
                ))}
            </ul>
          ) : null
        }
      />

      <ConfirmModal
        show={actionTarget?.type === 'delete'}
        title="Permanently delete"
        message={
          actionTarget?.row
            ? `Permanently delete "${actionTarget.row.record_name || 'this record'}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete Forever"
        variant="danger"
        onHide={() => !actionBusy && setActionTarget(null)}
        onConfirm={handleConfirmAction}
        busy={actionBusy}
        error={actionError}
      />
    </Container>
  );
};

export default RecycleBin;
